import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { motion, animate, useMotionValue } from "framer-motion";
import { getSpringConfig } from "@thangdevalone/meeting-grid-layout-core";
import { createParticipantAdaptiveGrid, getParticipantAdaptiveGridItemStyle } from "./participant-adaptive-grid";

const DEFAULT_PARTICIPANT_ADAPTIVE_GRID_SPRING_PRESET = "smooth";

const useStyles = makeStyles({
  root: {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    pointerEvents: "auto"
  },
  item: {
    position: "absolute",
    top: 0,
    left: 0,
    boxSizing: "border-box",
    width: "var(--participant-adaptive-grid-width)",
    height: "var(--participant-adaptive-grid-height)",
    minWidth: 0,
    minHeight: 0,
    transform: "translate(var(--participant-adaptive-grid-x), var(--participant-adaptive-grid-y))"
  }
});

function getElementSize(element) {
  if (!element) {
    return {
      width: 0,
      height: 0
    };
  }

  const rect = element.getBoundingClientRect();

  return {
    width: rect.width || element.clientWidth || 0,
    height: rect.height || element.clientHeight || 0
  };
}

function useMeasuredElementSize(enabled) {
  const elementRef = useRef(null);
  const [elementSize, setElementSize] = useState({ width: 0, height: 0 });
  const syncElementSize = useCallback(() => {
    const nextElementSize = getElementSize(elementRef.current);

    setElementSize(currentElementSize =>
      currentElementSize.width === nextElementSize.width && currentElementSize.height === nextElementSize.height
        ? currentElementSize
        : nextElementSize
    );
  }, []);
  const setMeasuredElementRef = useCallback(
    element => {
      elementRef.current = element;
      syncElementSize();
    },
    [syncElementSize]
  );

  useLayoutEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return undefined;
    }

    let resizeObserver;

    syncElementSize();
    window.addEventListener("resize", syncElementSize);

    if (typeof ResizeObserver !== "undefined" && elementRef.current) {
      resizeObserver = new ResizeObserver(syncElementSize);
      resizeObserver.observe(elementRef.current);
    }

    return () => {
      window.removeEventListener("resize", syncElementSize);
      resizeObserver?.disconnect();
    };
  }, [enabled, syncElementSize]);

  return [setMeasuredElementRef, elementSize];
}

function getDefaultItemKey(item, index) {
  return item?.id || index;
}

function getParticipantAdaptiveGridTransition(springPreset) {
  const springConfig = getSpringConfig(springPreset);

  return {
    type: springConfig.type || "spring",
    stiffness: springConfig.stiffness,
    damping: springConfig.damping,
    mass: springConfig.mass
  };
}

function ParticipantAdaptiveGridItemFrame({ children, className, disableAnimation, gridItem, springPreset }) {
  const itemStyle = getParticipantAdaptiveGridItemStyle(gridItem);
  const transition = useMemo(() => getParticipantAdaptiveGridTransition(springPreset), [springPreset]);
  const x = useMotionValue(gridItem.position.left);
  const y = useMotionValue(gridItem.position.top);
  const motionReadyRef = useRef(false);
  const targetX = gridItem.position.left;
  const targetY = gridItem.position.top;
  const targetWidth = gridItem.dimensions.width;
  const targetHeight = gridItem.dimensions.height;
  const dataAttributes = {
    "data-participant-adaptive-grid-float": gridItem.isFloat ? "true" : "false",
    "data-participant-adaptive-grid-index": gridItem.index,
    "data-participant-adaptive-grid-item": "",
    "data-participant-adaptive-grid-main": gridItem.isMain ? "true" : "false"
  };

  useLayoutEffect(() => {
    if (!motionReadyRef.current || disableAnimation) {
      x.set(targetX);
      y.set(targetY);
      motionReadyRef.current = true;
      return undefined;
    }

    const xAnimation = animate(x, targetX, transition);
    const yAnimation = animate(y, targetY, transition);

    return () => {
      xAnimation.stop();
      yAnimation.stop();
    };
  }, [disableAnimation, targetX, targetY, transition, x, y]);

  if (disableAnimation) {
    return (
      <div className={className} data-participant-adaptive-grid-motion="false" style={itemStyle} {...dataAttributes}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      animate={{ width: targetWidth, height: targetHeight }}
      className={className}
      data-participant-adaptive-grid-motion="true"
      initial={{ width: targetWidth, height: targetHeight }}
      style={{
        ...itemStyle,
        width: targetWidth,
        height: targetHeight,
        x,
        y
      }}
      transition={transition}
      {...dataAttributes}
    >
      {children}
    </motion.div>
  );
}

ParticipantAdaptiveGridItemFrame.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  disableAnimation: PropTypes.bool,
  gridItem: PropTypes.shape({
    dimensions: PropTypes.shape({
      height: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired
    }).isRequired,
    index: PropTypes.number.isRequired,
    isFloat: PropTypes.bool,
    isMain: PropTypes.bool,
    position: PropTypes.shape({
      left: PropTypes.number.isRequired,
      top: PropTypes.number.isRequired
    }).isRequired
  }).isRequired,
  springPreset: PropTypes.string.isRequired
};

function getTouchClientX(event) {
  return event.targetTouches?.[0]?.clientX;
}

export function ParticipantAdaptiveGrid({
  aspectRatio,
  bottomInset = 0,
  className,
  currentPage,
  currentVisiblePage,
  dimensions,
  disableAnimation = false,
  disableFloat,
  floatBreakpoints,
  floatHeight,
  floatWidth,
  gap,
  getItemKey = getDefaultItemKey,
  itemAspectRatios,
  itemClassName,
  items = [],
  layoutMode,
  maxItemsPerPage,
  maxVisible,
  onSwipeNextPage,
  onSwipePreviousPage,
  othersPosition,
  pinOnly,
  pinnedIndex,
  pipIndex,
  renderItem,
  renderOverlay,
  springPreset = DEFAULT_PARTICIPANT_ADAPTIVE_GRID_SPRING_PRESET,
  swipeEnabled = false,
  swipeMinDistance = 50,
  twoItemPinnedSplit
}) {
  const styles = useStyles();
  const touchStartXRef = useRef(null);
  const touchEndXRef = useRef(null);
  const shouldMeasure = !dimensions;
  const [setMeasuredElementRef, measuredDimensions] = useMeasuredElementSize(shouldMeasure);
  const effectiveDimensions = dimensions || measuredDimensions;
  const safeBottomInset = Number.isFinite(bottomInset) ? Math.max(0, bottomInset) : 0;
  const effectiveLayoutDimensions = useMemo(
    () => ({
      ...effectiveDimensions,
      height: Math.max(0, (effectiveDimensions.height || 0) - safeBottomInset)
    }),
    [effectiveDimensions, safeBottomInset]
  );
  const canSwipe = swipeEnabled && (onSwipeNextPage || onSwipePreviousPage);
  const handleTouchStart = useCallback(
    event => {
      if (!canSwipe) {
        return;
      }

      touchEndXRef.current = null;
      touchStartXRef.current = getTouchClientX(event);
    },
    [canSwipe]
  );
  const handleTouchMove = useCallback(
    event => {
      if (!canSwipe) {
        return;
      }

      touchEndXRef.current = getTouchClientX(event);
    },
    [canSwipe]
  );
  const handleTouchEnd = useCallback(() => {
    if (!canSwipe || !Number.isFinite(touchStartXRef.current) || !Number.isFinite(touchEndXRef.current)) {
      return;
    }

    const distance = touchStartXRef.current - touchEndXRef.current;

    if (distance > swipeMinDistance) {
      onSwipeNextPage?.();
    } else if (distance < -swipeMinDistance) {
      onSwipePreviousPage?.();
    }

    touchStartXRef.current = null;
    touchEndXRef.current = null;
  }, [canSwipe, onSwipeNextPage, onSwipePreviousPage, swipeMinDistance]);
  const grid = useMemo(
    () =>
      createParticipantAdaptiveGrid({
        aspectRatio,
        count: items.length,
        currentPage,
        currentVisiblePage,
        dimensions: effectiveLayoutDimensions,
        disableFloat,
        floatBreakpoints,
        floatHeight,
        floatWidth,
        gap,
        itemAspectRatios,
        layoutMode,
        maxItemsPerPage,
        maxVisible,
        othersPosition,
        pinOnly,
        pinnedIndex,
        pipIndex,
        twoItemPinnedSplit
      }),
    [
      aspectRatio,
      currentPage,
      currentVisiblePage,
      disableFloat,
      effectiveLayoutDimensions,
      floatBreakpoints,
      floatHeight,
      floatWidth,
      gap,
      itemAspectRatios,
      items.length,
      layoutMode,
      maxItemsPerPage,
      maxVisible,
      othersPosition,
      pinOnly,
      pinnedIndex,
      pipIndex,
      twoItemPinnedSplit
    ]
  );

  return (
    <div
      className={mergeClasses(styles.root, className)}
      data-participant-adaptive-grid=""
      data-participant-adaptive-grid-bottom-inset={safeBottomInset}
      data-participant-adaptive-grid-swipe={canSwipe ? "true" : "false"}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      ref={setMeasuredElementRef}
    >
      {grid.items.map(gridItem => {
        const item = items[gridItem.index];

        return (
          <ParticipantAdaptiveGridItemFrame
            className={mergeClasses(styles.item, itemClassName)}
            disableAnimation={disableAnimation}
            gridItem={gridItem}
            key={getItemKey(item, gridItem.index)}
            springPreset={springPreset}
          >
            {renderItem({ grid, gridItem, index: gridItem.index, item })}
          </ParticipantAdaptiveGridItemFrame>
        );
      })}
      {renderOverlay ? renderOverlay({ grid }) : null}
    </div>
  );
}

ParticipantAdaptiveGrid.propTypes = {
  aspectRatio: PropTypes.string,
  bottomInset: PropTypes.number,
  className: PropTypes.string,
  currentPage: PropTypes.number,
  currentVisiblePage: PropTypes.number,
  dimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  disableAnimation: PropTypes.bool,
  disableFloat: PropTypes.bool,
  floatBreakpoints: PropTypes.arrayOf(
    PropTypes.shape({
      height: PropTypes.number.isRequired,
      minWidth: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired
    })
  ),
  floatHeight: PropTypes.number,
  floatWidth: PropTypes.number,
  gap: PropTypes.number,
  getItemKey: PropTypes.func,
  itemAspectRatios: PropTypes.arrayOf(PropTypes.string),
  itemClassName: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  layoutMode: PropTypes.oneOf(["gallery", "spotlight"]),
  maxItemsPerPage: PropTypes.number,
  maxVisible: PropTypes.number,
  onSwipeNextPage: PropTypes.func,
  onSwipePreviousPage: PropTypes.func,
  othersPosition: PropTypes.oneOf(["left", "right", "top", "bottom"]),
  pinOnly: PropTypes.bool,
  pinnedIndex: PropTypes.number,
  pipIndex: PropTypes.number,
  renderItem: PropTypes.func.isRequired,
  renderOverlay: PropTypes.func,
  springPreset: PropTypes.oneOf(["snappy", "smooth", "gentle", "bouncy"]),
  swipeEnabled: PropTypes.bool,
  swipeMinDistance: PropTypes.number,
  twoItemPinnedSplit: PropTypes.bool
};
